/**
 * Pre-auth workspace branding — browser glue (White-label Phase 1, G1).
 *
 * The server already resolves the workspace for the current host and returns
 * its branding from GET /api/workspace/auth-config, but until now the only
 * client consumer (syncWorkspaceTenantAuth in lib/firebase.ts) read the
 * tenantId and DISCARDED the branding — so the sign-in/sign-up surfaces
 * rendered the build-time brand (ISATS on this deployment) on every host,
 * and a tenant's own users landed on another brand's front door.
 *
 * Flow:
 *   1. syncWorkspaceTenantAuth() hands the auth-config payload it already
 *      fetches at boot to applyAuthConfigBranding() — no second request.
 *   2. The pure merge (lib/preAuthBrandingCore) resolves the payload over
 *      the build-time brand per field.
 *   3. The merged brand is applied to the DOM through the SAME
 *      applyBrandToDOM() the app boot uses (CSS --brand-* vars + Tailwind
 *      blue scale, document.title, meta description, favicon) and exposed
 *      to React via usePreAuthBrand() for the surfaces that take colors /
 *      logos from JS values (AuthPage's inline styles and <img> sources).
 *
 * FALLBACK CONTRACT (the regression guardrail): the build-time brand is
 * the initial store state and is what App applies on mount. A failed
 * fetch, a 404/503 from auth-config (unknown host), a partial payload, or
 * a payload with no usable fields all leave the pre-auth surfaces
 * rendering EXACTLY today's defaults — render-default-first, swap when a
 * config resolves. The operating workspace's own auth-config returns its
 * ISATS branding, so its end-state render is its own brand, applied
 * through the same fields it already uses post-login.
 *
 * Payload fields deliberately NOT consumed pre-auth (see BUILD_STATE):
 * sidebarLogoUrl / invoiceLogoUrl / emailLogoUrl (post-login shell,
 * invoice/PDF and email surfaces — each already resolves workspace
 * branding in its own context), tenantId / authMode / signInMethods
 * (auth mechanics, owned by firebase.ts / the server), and terms/privacy
 * URLs (the workspace branding object carries no such fields; the
 * build-time legal links stay).
 */

import { useSyncExternalStore } from 'react';
import { brand, applyBrandToDOM, type BrandConfig } from './branding';
import {
  resolvePreAuthBrand,
  toBrandConfig as toBrandConfigShape,
  type AuthConfigBrandingPayload,
  type PreAuthBrand,
} from './preAuthBrandingCore';

export type { AuthConfigBrandingPayload, PreAuthBrand } from './preAuthBrandingCore';
export { resolvePreAuthBrand } from './preAuthBrandingCore';

/** The effective pre-auth brand rebuilt as a full BrandConfig (build-time
 *  brand supplies every field the workspace branding object lacks). */
export function toBrandConfig(effective: PreAuthBrand): BrandConfig {
  return toBrandConfigShape(effective, brand);
}

let currentBrand: PreAuthBrand = resolvePreAuthBrand(null, brand);
const listeners = new Set<() => void>();

export function getPreAuthBrand(): PreAuthBrand {
  return currentBrand;
}

export function subscribePreAuthBrand(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding for pre-auth surfaces (AuthPage). Re-renders when the
 *  host workspace's config resolves and replaces the defaults. */
export function usePreAuthBrand(): PreAuthBrand {
  return useSyncExternalStore(subscribePreAuthBrand, getPreAuthBrand, getPreAuthBrand);
}

/**
 * Entry point called by syncWorkspaceTenantAuth() with the auth-config
 * payload it already fetched. Merges + stores the effective brand, applies
 * it to the DOM, and notifies React subscribers.
 *
 * A null/undefined payload (fetch failed, non-OK response, unknown host)
 * resets the store to the build-time defaults and performs NO DOM writes —
 * App's boot effect owns applying the defaults, so a failure can never
 * paint anything but today's branding.
 */
export function applyAuthConfigBranding(
  config: AuthConfigBrandingPayload | null | undefined,
): PreAuthBrand {
  const next = resolvePreAuthBrand(config, brand);
  currentBrand = next;
  if (config && typeof document !== 'undefined') {
    applyBrandToDOM(toBrandConfig(next));
  }
  for (const listener of [...listeners]) listener();
  return next;
}
